import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { trackMatomoEvent } from "@/utils/matomo";

type TrackedRequestConfig = AxiosRequestConfig & {
  metadata?: {
    startedAt: number;
    endpoint: string;
    method: string;
    aggregate?: "refresh-token";
  };
};

const TRACKED_METHODS = new Set(["get", "post", "put", "patch", "delete"]);
const REFRESH_TOKEN_ENDPOINT = "usuarios/auth/refreshToken";
const originalCreate = axios.create.bind(axios);
let installed = false;
let pendingRefreshTokenCount = 0;
let flushListenersInstalled = false;

function normalizeEndpoint(baseURL: string | undefined, url: string | undefined) {
  const rawUrl = url ?? "";

  try {
    const parsed = new URL(rawUrl, baseURL || window.location.origin);
    return sanitizePath(parsed.pathname);
  } catch {
    return sanitizePath(rawUrl.split("?")[0] || "endpoint-desconhecido");
  }
}

function sanitizePath(path: string) {
  return path
    .replace(/^\/+/, "")
    .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/gi, ":uuid")
    .replace(/\b\d{11}\b/g, ":cpf")
    .replace(/\b\d{6,}\b/g, ":id")
    .replace(/\/\d+\b/g, "/:id")
    .slice(0, 180);
}

function shouldTrack(config: AxiosRequestConfig) {
  return TRACKED_METHODS.has((config.method ?? "get").toLowerCase());
}

function shouldAggregateEndpoint(endpoint: string) {
  return endpoint.toLowerCase() === REFRESH_TOKEN_ENDPOINT.toLowerCase();
}

function countRefreshTokenRestore(failed: boolean) {
  if (!failed) pendingRefreshTokenCount += 1;
}

export function flushRefreshTokenTracking() {
  if (pendingRefreshTokenCount <= 0) return;

  const count = pendingRefreshTokenCount;
  pendingRefreshTokenCount = 0;

  trackMatomoEvent({
    category: "Sessao",
    action: "refreshToken restaurado",
    name: `${REFRESH_TOKEN_ENDPOINT} (${count})`,
    value: count,
  });
}

function installRefreshTokenFlushListeners() {
  if (flushListenersInstalled || typeof window === "undefined") return;
  flushListenersInstalled = true;

  window.addEventListener("pagehide", flushRefreshTokenTracking);
  window.addEventListener("beforeunload", flushRefreshTokenTracking);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushRefreshTokenTracking();
  });
}

function trackRequestResult(config: TrackedRequestConfig | undefined, status: number, failed: boolean) {
  if (!config?.metadata) return;

  if (config.metadata.aggregate === "refresh-token") {
    countRefreshTokenRestore(failed);
    return;
  }

  const elapsedMs = performance.now() - config.metadata.startedAt;
  const result = failed ? "falha" : "sucesso";

  trackMatomoEvent({
    category: "Requisicao",
    action: `${config.metadata.method} ${result} ${status}`,
    name: config.metadata.endpoint,
    value: elapsedMs,
  });
}

function installInterceptors(instance: AxiosInstance) {
  instance.interceptors.request.use((config) => {
    if (!shouldTrack(config)) return config;

    const trackedConfig = config as TrackedRequestConfig;
    const endpoint = normalizeEndpoint(config.baseURL, config.url);
    trackedConfig.metadata = {
      startedAt: performance.now(),
      endpoint,
      method: (config.method ?? "get").toUpperCase(),
      aggregate: shouldAggregateEndpoint(endpoint) ? "refresh-token" : undefined,
    };
    return trackedConfig;
  });

  instance.interceptors.response.use(
    (response: AxiosResponse) => {
      trackRequestResult(response.config as TrackedRequestConfig, response.status, false);
      return response;
    },
    (error) => {
      trackRequestResult(error.config as TrackedRequestConfig | undefined, error.response?.status ?? 0, true);
      return Promise.reject(error);
    },
  );
}

export function installHttpTracking() {
  if (installed) return;
  installed = true;

  installRefreshTokenFlushListeners();
  installInterceptors(axios);

  axios.create = function createTrackedAxiosInstance(...args: Parameters<typeof axios.create>) {
    const instance = originalCreate(...args);
    installInterceptors(instance);
    return instance;
  };
}

installHttpTracking();
